import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageSquare, 
  Send, 
  Loader2, 
  MapPin, 
  Clock, 
  Calendar,
  Sparkles,
  ChevronRight,
  IndianRupee,
  Building2,
  AlertCircle,
  X
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

// Example prompts to show users
const EXAMPLE_PROMPTS = [
  "Book pickleball tomorrow evening around 7 PM",
  "I want to play badminton this Saturday morning",
  "Find me a court for 2 hours near Mira Road",
  "Pickleball slot for 4 people on Sunday afternoon"
];

export default function NaturalLanguageBooking({ onSelectOption, onClose }) {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showExamples, setShowExamples] = useState(true);

  const handleParse = async () => {
    if (!inputText.trim()) {
      toast.error('Please enter your booking request');
      return;
    }

    setLoading(true);
    setShowExamples(false);
    
    try {
      const response = await axios.post(`${API}/user/parse-booking`, {
        text: inputText
      });
      
      setResult(response.data);
      
      if (response.data.is_qna) {
        toast.success('Maksio answered your question!');
      } else if (response.data.parsed?.confidence < 0.5) {
        toast.warning('I understood part of your request. Please review the details.');
      } else if (response.data.available_options?.length > 0) {
        toast.success(`Found ${response.data.available_options.length} matching options!`);
      } else {
        toast.info('No exact matches found. Try adjusting your request.');
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error(error.response?.data?.detail || 'Failed to understand your request');
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setInputText(example);
    setShowExamples(false);
  };

  const handleSelectOption = (option) => {
    // Navigate to slot selection with pre-filled data from NLP parsing
    const parsed = result?.parsed;
    if (parsed?.sport && option) {
      // Build URL params with date and time preference
      const params = new URLSearchParams({
        game: parsed.sport,
        venue_id: option.venue_id,
        venue_name: option.venue_name
      });
      
      // Add parsed date (e.g., tomorrow's date)
      if (parsed.date) {
        params.set('date', parsed.date);
      }
      
      // Add time preference for auto-expanding the correct time category
      if (parsed.time_preference) {
        params.set('time', parsed.time_preference);
      }
      
      navigate(`/slots?${params.toString()}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleParse();
    }
  };

  const resetSearch = () => {
    setResult(null);
    setInputText('');
    setShowExamples(true);
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 rounded-2xl p-4 mb-6 border border-purple-200 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-sm">Ask Maksio – Your Smart Sports Companion</h3>
            <p className="text-xs text-slate-500">Powered by MAKS AI Intelligence</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-white/50 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        )}
      </div>

      {/* Input Area */}
      <div className="relative mb-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-violet-400" />
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Book pickleball tomorrow evening at 7 PM..."
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-purple-200 bg-white/80 backdrop-blur focus:border-violet-400 focus:ring-2 focus:ring-violet-200 transition-all text-sm"
              disabled={loading}
              data-testid="nlp-booking-input"
            />
          </div>
          <Button
            onClick={handleParse}
            disabled={loading || !inputText.trim()}
            className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-4 rounded-xl shadow-lg shadow-purple-500/25"
            data-testid="nlp-booking-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Example Prompts */}
      {showExamples && !result && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-2">Try saying:</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_PROMPTS.map((example, idx) => (
              <button
                key={idx}
                onClick={() => handleExampleClick(example)}
                className="text-xs px-3 py-1.5 bg-white/70 hover:bg-white border border-purple-200 rounded-full text-slate-600 hover:text-violet-600 transition-all"
              >
                "{example}"
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-6">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-600">Understanding your request...</p>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div className="space-y-4">
          {result.is_qna ? (
            <div className="bg-white/80 backdrop-blur rounded-xl p-3 border border-purple-100" data-testid="nlp-qna-block">
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Maksio says:
              </p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="nlp-qna-reply">{result.reply_text}</p>
            </div>
          ) : (
            <>
          {/* Parsed Intent Summary */}
          <div className="bg-white/80 backdrop-blur rounded-xl p-3 border border-purple-100">
            <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              I understood:
            </p>
            <div className="flex flex-wrap gap-2">
              {result.parsed?.sport && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                  🏸 {result.parsed.sport}
                </span>
              )}
              {result.parsed?.date_display && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  <Calendar className="w-3 h-3" />
                  {result.parsed.date_display}
                </span>
              )}
              {result.parsed?.time_preference && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  <Clock className="w-3 h-3" />
                  {result.parsed.start_time || result.parsed.time_preference}
                </span>
              )}
              {result.parsed?.location && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-100 text-rose-700 rounded-full text-xs font-medium">
                  <MapPin className="w-3 h-3" />
                  {result.parsed.location}
                </span>
              )}
              {result.parsed?.duration_hours && result.parsed.duration_hours > 1 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  {result.parsed.duration_hours} hours
                </span>
              )}
            </div>
            
            {/* Missing Info Warning */}
            {result.parsed?.missing_info?.length > 0 && (
              <div className="mt-2 flex items-start gap-2 text-xs text-amber-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Missing: {result.parsed.missing_info.join(', ')}</span>
              </div>
            )}
          </div>

          {/* Follow-up Question */}
          {result.follow_up_question && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-sm text-amber-800">{result.follow_up_question}</p>
            </div>
          )}

          {/* Available Options */}
          {result.available_options?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Best matches ({result.available_options.length} venues):
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {result.available_options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectOption(option)}
                    className="w-full bg-white hover:bg-violet-50 rounded-xl p-3 border border-slate-200 hover:border-violet-300 transition-all text-left group"
                    data-testid={`nlp-option-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800 text-sm group-hover:text-violet-600 transition-colors">
                          {option.venue_name}
                        </h4>
                        {option.address && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {option.address}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-emerald-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {option.available_slots?.length || 0} slots available
                          </span>
                          <span className="text-xs text-slate-400">|</span>
                          <span className="text-xs text-violet-600 flex items-center gap-0.5">
                            <IndianRupee className="w-3 h-3" />
                            {option.price_range}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-violet-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {result.available_options?.length === 0 && !result.follow_up_question && (
            <div className="text-center py-4">
              <p className="text-sm text-slate-500">No matching slots found. Try adjusting your request.</p>
            </div>
          )}
            </>
          )}

          {/* Reset Button */}
          <button
            onClick={resetSearch}
            className="w-full py-2 text-sm text-violet-600 hover:text-violet-800 hover:bg-violet-50 rounded-lg transition-colors"
          >
            Try another search
          </button>
        </div>
      )}
    </div>
  );
}
